/**
 * JobContactConversationModal
 *
 * WhatsApp-style 1:1 conversation thread scoped to a specific job contact.
 * Resolves the correct conversation/phone context on open via POST /voip/conversations/resolve,
 * then loads + sends messages. Technicians only see messages linked to their job, and real
 * phone numbers are redacted when call-masking is enabled.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle, Phone } from 'lucide-react';
import { apiService, apiErrorMessage } from '@/lib/api';
import { MessageDTOv2, PhoneNumberDTO, ResolveConversationResponse } from '@/types';
import { canSendSmsFrom, formatVoipNumberLabel } from '@/lib/phoneUtils';
import { useRealtime } from '@/providers/RealtimeProvider';
import { SmsConversationPanel, SmsAttachment } from './SmsConversationPanel';
import { SendSmsModal } from './SendSmsModal';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface JobContactConversationModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Job context for permissions/masking. Omit for contact-level threads (e.g. Contacts page). */
    jobId?: string;
    contactId: string;
    which: 'primary' | 'additional';
    masked: boolean;
    contactName: string;
    /** Only used as fallback display before resolve completes */
    recipientPhone?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function JobContactConversationModal({
    isOpen,
    onClose,
    jobId,
    contactId,
    which,
    contactName,
    recipientPhone,
}: JobContactConversationModalProps) {
    const { subscribeNewSms, subscribeSmsStatusUpdated, consumeLatestSmsStatus } = useRealtime();

    const [resolved, setResolved] = useState<ResolveConversationResponse | null>(null);
    const [resolveError, setResolveError] = useState<string | null>(null);
    const [resolving, setResolving] = useState(false);

    const [messages, setMessages] = useState<MessageDTOv2[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesLoaded, setMessagesLoaded] = useState(false);
    const [messagesError, setMessagesError] = useState<string | null>(null);

    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);

    // The numbers this user may text from (a technician: only numbers technicians can use), the
    // job's line first. More than one = a line picker in the header.
    const [numbers, setNumbers] = useState<PhoneNumberDTO[]>([]);
    // Set once the user picks a line by hand: an empty thread on it then stays in this view instead
    // of jumping to the composer (that fallback is for opening a contact with no thread at all).
    const [lineChosen, setLineChosen] = useState(false);
    const lines = numbers.filter(canSendSmsFrom);
    // ponytail: don't proxy MMS off provider === twilio — Telnyx numbers have capabilitiesMms too,
    // and that's what VoipMessaging / SendSmsModal already use.
    const supportsMms = numbers.find(n => n.id === resolved?.fromPhoneNumberId)?.capabilitiesMms ?? false;

    // Composer modal: opened automatically when there's no thread yet (empty/new
    // conversation), or manually via the header "Change number" button.
    // ponytail: closing it (sent or cancelled) always closes this modal too — no
    // fallback into the conversation view. Re-resolving on send unmounted the composer
    // mid-close, which swallowed its popstate and re-opened it.
    const [showSendModal, setShowSendModal] = useState(false);

    // Stacked modal layer (z-index + depth) — parents handle back navigation.
    const layer = useModalLayer({ enabled: isOpen, onClose, backNavigation: false });

    // No existing thread to show — only decided once the message load has settled
    // (messagesLoaded), so we never flash the composer during the load window.
    const messagesEmpty = !!resolved && messagesLoaded && !resolveError && messages.length === 0;

    useEffect(() => {
        if (!isOpen) return;
        apiService.getPhoneNumbers(jobId).then(setNumbers).catch(() => setNumbers([]));
    }, [isOpen, jobId]);

    // ── Resolve on open ───────────────────────────────────────────────────────

    // fromPhoneNumberId: the line picked in the header. The server then returns THAT line's thread,
    // so the history shown and the number sending always match.
    const doResolve = useCallback(async (fromPhoneNumberId?: string) => {
        setResolving(true);
        setResolveError(null);
        try {
            const result = await apiService.resolveConversation({ contactId, which, jobId, fromPhoneNumberId });
            setResolved(result);
        } catch (err: any) {
            setResolveError(err?.response?.data?.error ?? 'Failed to load conversation context.');
        } finally {
            setResolving(false);
        }
    }, [contactId, which, jobId]);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when closed
            setResolved(null);
            setMessages([]);
            setMessagesLoaded(false);
            setSendError(null);
            setResolveError(null);
            setMessagesError(null);
            setShowSendModal(false);
            setLineChosen(false);
            return;
        }
        doResolve();
    }, [isOpen, doResolve]);

    // ── Load messages when conversation is resolved ───────────────────────────

    useEffect(() => {
        if (!resolved) return;
        // Resolved with no conversation id → genuinely new/empty thread, nothing to load.
        if (!resolved.conversationId) {
            setMessages([]);
            setMessagesLoaded(true);
            return;
        }
        let cancelled = false;
        setMessagesLoading(true);
        setMessagesLoaded(false);
        setMessagesError(null);
        apiService
            .getConversationMessagesV2(resolved.conversationId, 50, undefined, jobId)
            .then((data) => {
                if (!cancelled) {
                    setMessages(data.items);
                    apiService.markConversationReadV2(resolved.conversationId!).catch(() => {});
                }
            })
            .catch(() => {
                if (!cancelled) setMessagesError('Failed to load messages.');
            })
            .finally(() => {
                if (!cancelled) {
                    setMessagesLoading(false);
                    setMessagesLoaded(true);
                }
            });
        return () => { cancelled = true; };
    }, [resolved, jobId]);

    // ── Realtime subscriptions ────────────────────────────────────────────────

    useEffect(() => {
        if (!resolved?.conversationId || !isOpen) return;

        const unsubNew = subscribeNewSms((event) => {
            if (event.conversation_id !== resolved.conversationId) return;
            apiService
                .getConversationMessagesV2(resolved.conversationId!, 50, undefined, jobId)
                .then((data) => setMessages(data.items))
                .catch(() => {});
            apiService.markConversationReadV2(resolved.conversationId!).catch(() => {});
        });

        const unsubStatus = subscribeSmsStatusUpdated((event) => {
            setMessages((prev) =>
                prev.map((m) =>
                    m.id === event.message_id ? { ...m, status: event.status.toLowerCase() } : m
                )
            );
        });

        return () => {
            unsubNew();
            unsubStatus();
        };
    }, [resolved?.conversationId, isOpen, subscribeNewSms, subscribeSmsStatusUpdated, resolved?.fromPhoneNumberId]);

    // ── Empty conversation → composer ─────────────────────────────────────────
    // No thread yet: drop the user straight into the Send SMS modal (which lets
    // them pick the from/to number) instead of an empty conversation frame.
    useEffect(() => {
        if (messagesEmpty && !lineChosen) setShowSendModal(true);
    }, [messagesEmpty, lineChosen]);

    // ── Send ──────────────────────────────────────────────────────────────────

    const handleSendFromPanel = async (body: string, attachments?: SmsAttachment[]) => {
        if (!resolved || sending) return;

        const objectKeys = attachments?.map(a => a.objectKey);
        const contentTypes = attachments?.map(a => a.contentType);
        const optimisticMedia = (attachments ?? []).map((a, i) => ({
            id: `optimistic-${i}`,
            contentType: a.contentType,
            downloadUrl: a.previewUrl,
            status: 'uploaded' as const,
        }));
        const previewUrlsToRevoke = (attachments ?? []).map(a => a.previewUrl).filter(Boolean) as string[];
        const revokePreviewUrls = () => previewUrlsToRevoke.forEach(u => URL.revokeObjectURL(u));

        setSending(true);
        setSendError(null);

        const tempId = `temp-${Date.now()}`;
        const optimistic: MessageDTOv2 = {
            id: tempId,
            phoneNumberId: resolved.fromPhoneNumberId,
            provider: '',
            direction: 'outbound',
            fromE164: resolved.fromE164,
            toE164: resolved.peerE164 ?? resolved.peerDisplay,
            body: body ?? '',
            numSegments: 1,
            mediaCount: objectKeys?.length ?? 0,
            status: 'sending',
            createdAt: new Date().toISOString(),
            media: optimisticMedia,
        };
        setMessages((prev) => [optimistic, ...prev]);

        try {
            const result = await apiService.sendMessageV2({
                fromPhoneNumberId: resolved.fromPhoneNumberId,
                ...(resolved.masked && resolved.contactRef
                    ? { toContactRef: resolved.contactRef }
                    : { toE164: resolved.peerE164! }),
                body: body || undefined,
                jobId,
                mediaObjectKeys: objectKeys,
                mediaObjectContentTypes: contentTypes,
            });

            if (result.status === 'quota_blocked') {
                setSendError(result.reason ?? 'Monthly outbound quota exhausted');
                setMessages((prev) => prev.filter((m) => m.id !== tempId));
                revokePreviewUrls();
            } else if (result.status === 'invalid') {
                setSendError(result.reason ?? result.errorMessage ?? 'Invalid request.');
                setMessages((prev) => prev.filter((m) => m.id !== tempId));
                revokePreviewUrls();
            } else if (result.status === 'failed') {
                setSendError(result.errorMessage ?? 'Send failed. Please try again.');
                setMessages((prev) => prev.filter((m) => m.id !== tempId));
                revokePreviewUrls();
            } else {
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === tempId
                            ? { ...m, id: result.messageId, status: result.status, providerMessageId: result.providerMessageId }
                            : m
                    )
                );
                // If this was the first message, re-resolve to get the real conversationId
                if (!resolved.conversationId) {
                    doResolve();
                }
                const pendingStatus = consumeLatestSmsStatus(result.messageId);
                if (pendingStatus) {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === result.messageId
                                ? { ...m, status: pendingStatus.status.toLowerCase() }
                                : m
                        )
                    );
                }
            }
        } catch (error: unknown) {
            setSendError(apiErrorMessage(error, 'Failed to send. Please try again.'));
            setMessages((prev) => prev.filter((m) => m.id !== tempId));
            revokePreviewUrls();
        } finally {
            setSending(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (!isOpen) return null;

    // While resolving, show a neutral backdrop so the conversation frame never
    // flashes before we know which modal to display.
    if (resolving) {
        return (
            <div style={layer.style} className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm safe-area-top safe-area-bottom" />
        );
    }

    // Once resolved as VoipMs, delegate entirely to the legacy SendSmsModal
    if (resolved?.provider?.toLowerCase() === 'voipms') {
        return (
            <SendSmsModal
                isOpen
                onClose={onClose}
                recipientPhone={resolved.masked ? '' : (resolved.peerE164 ?? recipientPhone ?? '')}
                recipientName={resolved.contactName || contactName}
                recipientType="Client"
                jobId={jobId}
                toContactRef={
                    resolved.masked && resolved.contactRef ? resolved.contactRef : undefined
                }
            />
        );
    }

    const displayName = resolved?.contactName || contactName;
    const displayPhone = resolved?.peerDisplay ?? recipientPhone ?? '';

    // Composer open (empty-thread fallback or "Change number") → show only the
    // Send SMS modal, stacked one layer above this conversation modal.
    if (showSendModal) {
        return (
            <LayerDepthContext.Provider value={layer.depth}>
                <SendSmsModal
                    isOpen
                    onClose={onClose}
                    recipientPhone={resolved?.masked ? '' : (resolved?.peerE164 ?? recipientPhone ?? '')}
                    recipientName={resolved?.contactName || contactName}
                    recipientType="Client"
                    jobId={jobId}
                    toContactRef={resolved?.masked && resolved?.contactRef ? resolved.contactRef : undefined}
                />
            </LayerDepthContext.Provider>
        );
    }

    return (
        <div style={layer.style} className="fixed inset-0 flex flex-col bg-white safe-area-top sm:bg-black/40 sm:backdrop-blur-sm sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-xl flex flex-col overflow-hidden flex-1 min-h-0 sm:flex-none sm:h-[88dvh] sm:max-h-[88dvh]">

                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-secondary-200 bg-white flex-shrink-0">
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-secondary-900 truncate">{displayName}</p>
                        {displayPhone && (
                            <p className="text-xs text-secondary-400 truncate">{displayPhone}</p>
                        )}
                        {resolved && lines.length > 1 && (
                            <label className="mt-1 flex items-center gap-1.5 text-xs text-secondary-500">
                                From
                                <select
                                    value={resolved.fromPhoneNumberId}
                                    disabled={resolving}
                                    onChange={(e) => {
                                        setLineChosen(true);
                                        void doResolve(e.target.value);
                                    }}
                                    className="min-w-0 max-w-[14rem] truncate rounded-md border border-secondary-200 bg-white px-1.5 py-0.5 text-xs text-secondary-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                >
                                    {lines.map(n => (
                                        <option key={n.id} value={n.id}>{formatVoipNumberLabel(n.e164Number, n.displayName)}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    {resolved && (
                        <button
                            onClick={() => setShowSendModal(true)}
                            className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 flex-shrink-0"
                        >
                            <Phone className="w-3.5 h-3.5" />
                            Change number
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-secondary-100 text-secondary-500 flex-shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Resolve error */}
                {resolveError && !resolving && (
                    <div className="flex-1 flex items-center justify-center p-6">
                        <div className="flex items-center gap-2 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg px-3 py-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{resolveError}</span>
                        </div>
                    </div>
                )}

                {/* Messages error banner (shown above panel when message load fails) */}
                {!resolveError && messagesError && (
                    <div className="flex-shrink-0 px-4 pt-3">
                        <div className="flex items-center gap-2 text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg px-3 py-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{messagesError}</span>
                        </div>
                    </div>
                )}

                {/* Conversation panel */}
                {!resolveError && (
                    <SmsConversationPanel
                        messages={messages}
                        loading={resolving || messagesLoading}
                        sending={sending}
                        sendError={sendError}
                        onClearSendError={() => setSendError(null)}
                        onSend={handleSendFromPanel}
                        disabled={!resolved || resolving}
                        supportsMms={supportsMms}
                        composerClassName="safe-area-bottom-padded sms-composer-footer"
                        sendOnEnter={false}
                    />
                )}
            </div>
        </div>
    );
}
